import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { FC, Fragment, useContext, useState } from 'react';
import { getTableStyle } from './transactionsTable.style';
import { TransactionDetailsDialog } from '../../TransactionDetailsDialog/TransactionDetailsDialog';
import { LocalizationContext } from '@context/LocalizationContext';
import { formatDate } from 'src/Utils/dateUtils';
import { InvestigationTransaction } from './transactions.types';
import { convertCrDrToType } from './transactions.utils';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from 'src/types/RefineAnalyticsEvents';

interface TransactionsTableProps {
	rows: InvestigationTransaction[];
}

export const TransactionsTable: FC<TransactionsTableProps> = ({ rows }) => {
	const isMobile = useMediaQuery('(max-width: 600px)');
	const theme: any = useTheme();
	const { table } = getTableStyle({ isMobile: isMobile, theme: theme });
	const [transactionDetailsDialogOpen, setTransactionDetailsDialogOpen] = useState(false);
	const [transactionDetails, setTransactionDetails] = useState<InvestigationTransaction>();
	const {
		TransactionDetails: {
			AccountTitle,
			AmountTitle,
			DateTitle,
			DescriptionTitle,
			TypeTitle,
			DetailsButton,
			OppositePartyTitle,
			MoneyIn,
			MoneyOut,
		},
	} = useContext(LocalizationContext);
	const { trackRefineEvent } = useAnalytics();

	const secondaryTextColor = theme.palette.typographyText.textSecondary;
	const handleTransactionDetailsDialogOpen = (transactionDetails: any) => {
		setTransactionDetailsDialogOpen(true);
		setTransactionDetails(transactionDetails);
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.TRANSACTION_DETAILS_OPEN, {
			transactionType: transactionDetails?.transactionType ?? transactionDetails.description,
		});
	};

	const showOppPartyNameColumn = rows.some((row) => row?.oppPartyName?.length);

	return (
		<Fragment>
			<TableContainer data-cy={'pqTransactionsTable'} sx={table}>
				<Table aria-label="simple table">
					{!isMobile && (
						<TableHead>
							<TableRow>
								<TableCell align="left">
									<Typography variant={'caption'} color={secondaryTextColor}>
										{AmountTitle}
									</Typography>
								</TableCell>
								<TableCell align="left">
									<Typography variant={'caption'} color={secondaryTextColor}>
										{DateTitle}
									</Typography>
								</TableCell>

								<TableCell align="left">
									<Typography variant={'caption'} color={secondaryTextColor}>
										{DescriptionTitle}
									</Typography>
								</TableCell>
								<TableCell align="left">
									<Typography variant={'caption'} color={secondaryTextColor}>
										{TypeTitle}
									</Typography>
								</TableCell>
								<TableCell align="left">
									<Typography variant={'caption'} color={secondaryTextColor}>
										{AccountTitle}
									</Typography>
								</TableCell>
								{showOppPartyNameColumn && (
									<TableCell align="left">
										<Typography variant={'caption'} color={secondaryTextColor}>
											{OppositePartyTitle}
										</Typography>
									</TableCell>
								)}
							</TableRow>
						</TableHead>
					)}

					<TableBody>
						{rows.map((row) => (
							<TableRow data-cy={'pqTransactionsTableRow'} key={row.executionLocalDateTime}>
								<TableCell align="left">
									<Typography variant={'body1'}>{`$${row.acctCurrAmount}`}</Typography>
								</TableCell>
								<TableCell align="left">
									<Typography variant={'body1'}>{formatDate(row.executionLocalDateTime)}</Typography>
								</TableCell>
								{isMobile ? (
									<TableCell>
										<Typography
											color={'#5977EB'}
											sx={{ textDecoration: 'underline' }}
											onClick={() => handleTransactionDetailsDialogOpen(row)}>
											{DetailsButton}
										</Typography>
									</TableCell>
								) : (
									<Fragment>
										<TableCell align="left">
											<Typography variant={'body1'}>
												{row?.transactionType ? row.transactionType : row.description}
											</Typography>
										</TableCell>
										<TableCell align="left">
											<Typography variant={'body1'}>
												{convertCrDrToType(row.crDrInd, {
													moneyIn: MoneyIn,
													moneyOut: MoneyOut,
												})}
											</Typography>
										</TableCell>
										<TableCell align="left">
											<Typography variant={'body1'}>{row.accountKey}</Typography>
										</TableCell>
										{showOppPartyNameColumn && (
											<TableCell align="left">
												<Typography variant={'body1'}>
													{row?.oppPartyName ? row.oppPartyName : row.oppWireParty}
												</Typography>
											</TableCell>
										)}
									</Fragment>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{transactionDetails && (
				<TransactionDetailsDialog
					transactionDetails={transactionDetails}
					open={transactionDetailsDialogOpen}
					handleClose={() => setTransactionDetailsDialogOpen(false)}
				/>
			)}
		</Fragment>
	);
};
